import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

import { useGetLogsMutation, type GetLogsResponse } from '@app/api/Logger';
import { useUserPrint } from '@app/hooks';
import type {
  Filters,
  HandleGetEmailLogsProps,
} from '@app/pages/EmailLogs/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import { getEmailLogsColumns } from '@pages/EmailLogs/data';

const defaultFilters = {
  dateS: dayjs().subtract(7, 'day').format('YYYY-MM-DDTHH:mm:ss'),
  dateE: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
  module: '',
  level: '',
  searchText: '',
};
const defaultPage = 1;
const defaultPageSize = 20;

export const useEmailLogs = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [data, setData] = useState<GetLogsResponse | null>(null);
  const [currentLogId, setCurrentLogId] = useState<number | null>(null);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);

  const [getEmailLogs, { isLoading }] = useGetLogsMutation();

  const handleGetEmailLogs = useCallback(
    async ({ filters, page, pageSize }: HandleGetEmailLogsProps) => {
      try {
        const result = await getEmailLogs({
          ...filters,
          page,
          pageSize,
          ActiveBranchId,
        }).unwrap();

        setData(result);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getEmailLogs, showNotification]
  );

  const onReset = useCallback(() => {
    setFilters(defaultFilters);
    handleGetEmailLogs({
      filters: defaultFilters,
      page: defaultPage,
      pageSize: defaultPageSize,
    });
  }, [handleGetEmailLogs]);

  const onSearch = () => {
    handleGetEmailLogs({
      filters,
      page: defaultPage,
      pageSize: defaultPageSize,
    });
  };

  const handleLogModalClose = () => {
    setIsLogModalOpen(false);
    setCurrentLogId(null);
  };

  const openLog = (id: number) => {
    setCurrentLogId(id);
    setIsLogModalOpen(true);
  };

  const columns = getEmailLogsColumns({ openLog });

  const handleTableChange = (page: number, pageSize: number) => {
    handleGetEmailLogs({
      filters,
      page,
      pageSize,
    });
  };

  return {
    onSearch,
    onReset,
    columns,
    isLoading,
    handleGetEmailLogs,
    defaultFilters,
    defaultPage,
    defaultPageSize,
    data,
    filters,
    setFilters,
    handleTableChange,
    currentLogId,
    isLogModalOpen,
    handleLogModalClose,
  };
};
